import { useLazyQuery } from '@apollo/client';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import {
  GetAssetByIdDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useTranslation } from 'react-i18next';
import { useFormCustomisation } from 'src/hooks/forms/useFormCustomisation';

import { toLocalDate } from '@/utils/dateUtils';
import { getFriendlyId } from '@/utils/friendlyId';
import { getContributorValue } from '@/utils/pdf/contributorValue';
import { getDepartmentsValue } from '@/utils/pdf/departmentValue';
import { createDocument } from '@/utils/pdf/document';
import { download } from '@/utils/pdf/downloader';
import { createField } from '@/utils/pdf/field';
import { createHeading, createSubHeading } from '@/utils/pdf/headings';
import { getOwnerValue } from '@/utils/pdf/ownerValue';
import { getTagsValue } from '@/utils/pdf/tagsValue';
import { twoColumns } from '@/utils/pdf/twoColumns';
import useCustomAttributeDataForExport from '@/utils/pdf/useCustomAttributeDataForExport';

const useExporter = (assetId: string): [() => void, { loading: boolean }] => {
  const { getStandardFieldLabel } = useFormCustomisation(['asset']);
  const [getCustomAttribute, customAttributesLoading] =
    useCustomAttributeDataForExport(Parent_Type_Enum.Asset);

  const { t } = useTranslation(['common']);

  const { getLabel: getStatusLabel } = useRating('asset_status');
  const [getAsset, getAssetResult] = useLazyQuery(GetAssetByIdDocument, {
    variables: {
      _eq: assetId,
    },
  });

  const loading = getAssetResult.loading || customAttributesLoading;
  const exportFunc = async () => {
    const { data: assetData } = await getAsset();
    const asset = assetData?.asset?.[0];

    if (!asset) {
      return;
    }

    const title = `${asset.Title} (${getFriendlyId(
      Parent_Type_Enum.Asset,
      asset.SequentialId
    )})`;

    const detailFields = [
      createField(getStandardFieldLabel('asset', 'Title'), asset.Title),
      createField(
        getStandardFieldLabel('asset', 'Description'),
        asset.Description
      ),
      createField(
        getStandardFieldLabel('asset', 'Owners'),
        getOwnerValue(asset)
      ),
      createField(
        getStandardFieldLabel('asset', 'Contributors'),
        getContributorValue(asset)
      ),
      createField(
        getStandardFieldLabel('asset', 'Status'),
        getStatusLabel(asset.Status)
      ),
      createField(
        getStandardFieldLabel('asset', 'AssetType'),
        asset.AssetType ?? ''
      ),
      createField(
        getStandardFieldLabel('asset', 'SerialNumber'),
        asset.SerialNumber ?? ''
      ),
      createField(
        getStandardFieldLabel('asset', 'PurchaseDate'),
        toLocalDate(asset.PurchaseDate)
      ),
      createField(
        getStandardFieldLabel('asset', 'Location'),
        asset.Location ?? ''
      ),
      createField(
        getStandardFieldLabel('asset', 'Cost'),
        asset.Cost?.toString() ?? ''
      ),
      createField(
        getStandardFieldLabel('asset', 'DepreciationMethod'),
        asset.DepreciationMethod ?? ''
      ),
      createField(
        getStandardFieldLabel('asset', 'Assignee'),
        asset.assigneeUser?.FriendlyName ?? ''
      ),
      createField(getStandardFieldLabel('asset', 'tags'), getTagsValue(asset)),
      createField(
        getStandardFieldLabel('asset', 'departments'),
        getDepartmentsValue(asset)
      ),
      ...(await getCustomAttribute(asset)),
    ];

    const docDefinition = createDocument(title, [
      createHeading(title),
      createSubHeading(t('details')),
      twoColumns(detailFields),
    ]);
    download(docDefinition);
  };

  return [exportFunc, { loading }];
};

export default useExporter;
